import { TLocation } from 'types/mdm';
import { TAuthUser } from 'types/auth';

/**
 * District-specific access helpers (Spec #2a).
 *
 * Location levels are fixed by convention (confirmed against CaseAddressForm and
 * Spec §6): Province = 1, District = 2, Commune = 3, Village = 4.
 */
export const PROVINCE_LEVEL = 1;
export const DISTRICT_LEVEL = 2;

/** Role name that bypasses all district scoping (Spec §6). */
export const ADMINISTRATOR_ROLE = 'administrator';

export type TDistrictAccessMode = 'full' | 'single' | 'multi';

/** Either the logged-in auth user or any user-like object carrying role + location_ids. */
type TScopedUser =
  | (Pick<TAuthUser, 'user_role'> & { location_ids?: number[] })
  | null
  | undefined;

export const isAdministrator = (user: TScopedUser): boolean =>
  (user?.user_role?.name || '').trim().toLowerCase() === ADMINISTRATOR_ROLE;

export const getAssignedDistrictIds = (user: TScopedUser): number[] =>
  Array.isArray(user?.location_ids) ? (user?.location_ids as number[]) : [];

/**
 * Resolve a user's district access mode.
 *
 * Defensive degradation: a missing/empty `location_ids` (e.g. backend not yet
 * returning the field) resolves to `full` so no UI is locked before the backend ships.
 */
export const getDistrictAccessMode = (
  user: TScopedUser
): TDistrictAccessMode => {
  if (isAdministrator(user)) return 'full';
  const ids = getAssignedDistrictIds(user);
  if (ids.length === 1) return 'single';
  if (ids.length > 1) return 'multi';
  return 'full';
};

export const isDistrictLevel = (location: TLocation): boolean =>
  location?.location_level?.level === DISTRICT_LEVEL;

export const isProvinceLevel = (location: TLocation): boolean =>
  location?.location_level?.level === PROVINCE_LEVEL;

export const getDistrictLocations = (locations: TLocation[]): TLocation[] =>
  (locations || []).filter(isDistrictLevel);

export const getProvinceLocations = (locations: TLocation[]): TLocation[] =>
  (locations || []).filter(isProvinceLevel);

/** District-level locations the user is assigned to. */
export const getAssignedDistrictLocations = (
  locations: TLocation[],
  user: TScopedUser
): TLocation[] => {
  const ids = getAssignedDistrictIds(user);
  return getDistrictLocations(locations).filter(d =>
    ids.includes(d.id as number)
  );
};

/** Provinces that are parents of the user's assigned districts. */
export const getAssignedProvinceLocations = (
  locations: TLocation[],
  user: TScopedUser
): TLocation[] => {
  const parentIds = Array.from(
    new Set(
      getAssignedDistrictLocations(locations, user)
        .map(d => d.parent_id)
        .filter((id): id is number => id != null)
    )
  );
  return getProvinceLocations(locations).filter(p =>
    parentIds.includes(p.id as number)
  );
};

/** Khmer name by default; English (`name_alt`) when the UI language is English. */
export const getLocationName = (
  location: TLocation,
  isEnglish: boolean
): string => (!isEnglish ? location.name : location.name_alt || location.name);

/** Map a list of district IDs to their display names (order preserved). */
export const getDistrictNames = (
  locations: TLocation[],
  ids: number[],
  isEnglish: boolean
): string[] => {
  const byId = new Map<number, TLocation>();
  getDistrictLocations(locations).forEach(d => byId.set(d.id as number, d));
  return (ids || [])
    .map(id => byId.get(id))
    .filter((d): d is TLocation => !!d)
    .map(d => getLocationName(d, isEnglish));
};
